import React from "react";
import clsx from "clsx";

export interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    type?: 'date' | 'datetime-local';
    isInvalid?: boolean;
}

// Drop-in pengganti <input type="date"> / <Form.Control type="date"> yang
// membuka native date picker saat area manapun di dalam input diklik (bukan
// cuma ikon kalender kecil di ujung kanan) -- input keyboard manual tetap
// jalan seperti biasa karena elemennya tetap <input> native, cuma nambah
// handler klik. showPicker() butuh gesture klik langsung & tidak didukung
// semua browser (Safari) makanya dibungkus try/catch supaya fallback diam-diam
// ke perilaku klik-icon-native kalau tidak didukung / elemen disabled/readOnly.
const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
    ({ type = 'date', className, isInvalid, onMouseDown, disabled, readOnly, ...rest }, ref) => {
        const localRef = React.useRef<HTMLInputElement | null>(null);

        const setRefs = (node: HTMLInputElement | null) => {
            localRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        };

        const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
            onMouseDown?.(e);
            if (disabled || readOnly) return;
            try {
                localRef.current?.showPicker?.();
            } catch {
                // Browser tidak dukung showPicker() (mis. Safari) -- biarkan
                // perilaku native (klik icon kalender) yang jalan.
            }
        };

        return (
            <input
                {...rest}
                type={type}
                ref={setRefs}
                disabled={disabled}
                readOnly={readOnly}
                onMouseDown={handleMouseDown}
                className={clsx(className ?? 'form-control app-form-control rounded-1', isInvalid && 'is-invalid')}
            />
        );
    }
);

DateInput.displayName = "DateInput";

export default DateInput;
